import {
  FilterQuery,
  ProjectFields,
  YesObject
} from '@yes.technology/react-toolkit'
import { FilterFieldsResponse } from 'shared/hooks/useFilterFields/useFilterFields'
import { FieldSettings } from 'shared/types'
import { setProjectFields } from 'site/Renderer/components/DataTables/DataTables'
import useTableLayouts from 'site/Renderer/components/DataTables/hooks/useTableLayouts'
import { GroupedFieldValues } from 'site/Renderer/components/FilterFields/types'
import { RelationConfigProps } from 'site/Renderer/components/ObjectView/components/types/object-view'
import useObjectFields from 'site/Renderer/components/ObjectView/hooks/useObjectFields'
import { useSiteState } from 'siteState/shared'
import {
  useFetchYesObjects,
  usePopulateObjectRelations
} from 'yesObject/shared'
import useRelationsTableLayouts from '../useRelationsTableLayouts/useRelationsTableLayouts'

type UseReportDataProps = {
  filteractionUuid?: string
  enabled?: boolean
  relationConfig?: RelationConfigProps
  fieldSettings?: FieldSettings
  classificationUuid?: string
  reportType: 'list' | 'card'
  yesObject?: YesObject
  layoutClassificationUuid?: string
}

export default function useReportData({
  filteractionUuid,
  enabled,
  relationConfig,
  fieldSettings,
  classificationUuid,
  reportType,
  yesObject,
  layoutClassificationUuid
}: UseReportDataProps) {
  const [fieldValues] = useSiteState<GroupedFieldValues>('field-values')
  const [filterFields] = useSiteState<FilterFieldsResponse>('filter-fields')
  const [filterQuery] = useSiteState<FilterQuery>('filter-query')
  const [filterComposition] = useSiteState<string>('field-composition')
  const [objectclassUuid] = useSiteState<string>('objectclassuuid')
  const [filteractionName] = useSiteState<string>('filteraction-name')

  const [tableLayout] = useTableLayouts({ layoutClassificationUuid })
  const projectFields: ProjectFields = setProjectFields(tableLayout)
  const hasProjectFields =
    layoutClassificationUuid && Object.keys(projectFields).length > 0

  const {
    yesObjects: filterYesObjects,
    isLoading: isLoadingYesObjects,
    isFetched: isFetchedYesObjects,
    error: errorYesObjects
  } = useFetchYesObjects({
    filterBody: {
      query: filterQuery,
      objectclass: objectclassUuid,
      skip_metadata: true,
      ...(hasProjectFields ? { project_fields: projectFields } : {})
    },
    filteractionUuid,
    initialPagination: {},
    enabled: enabled && !yesObject
  })

  const yesObjects = yesObject ? [yesObject] : filterYesObjects

  const objectFields = useObjectFields({
    objectclassUuid,
    classificationUuid,
    fieldSettings,
    enabled
  })

  const {
    relationsTableLayouts,
    isLoading: isLoadingRelationsLayouts,
    isFetched: isFetchedRelationsLayouts,
    error: errorRelationsLayout
  } = useRelationsTableLayouts({
    relationConfig,
    enabled: reportType === 'card' && !!relationConfig
  })

  const {
    data,
    isFetching: isLoadingPopulateObjectRelations,
    isFetched: isFetchedPopulateObjectRelations,
    error: errorPopulateObjectRelations
  } = usePopulateObjectRelations({
    objects: yesObjects,
    relationFields: objectFields.relationFields,
    enabled: enabled && isFetchedYesObjects
  })

  return {
    yesObjects: data,
    requestLoading:
      isLoadingYesObjects ||
      isLoadingRelationsLayouts ||
      isLoadingPopulateObjectRelations ||
      objectFields.isLoading,
    requestFetched:
      isFetchedYesObjects &&
      objectFields.isFetched &&
      (isFetchedRelationsLayouts || reportType !== 'card' || !relationConfig) &&
      isFetchedPopulateObjectRelations,
    error:
      errorYesObjects ||
      errorRelationsLayout ||
      errorPopulateObjectRelations ||
      objectFields.error,
    objectFields,
    fieldValues,
    filterFields,
    filterComposition,
    filteractionName,
    relationsTableLayouts,
    hasRequiredReportData: !!filterQuery
  }
}
